// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from '../ui/Snackbars';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getEmployeeEmails } from '../../utils/oauth.js';
import { postTravelDocumentImage, postEmployeeTravelDocument } from '../../utils/end-points.js';
import { FileUploader } from "react-drag-drop-files";
import { getCountryList, getDateForDB } from '../../utils/formatting';
import { IconButton } from "@material-ui/core";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { makeStyles } from '@mui/styles';
import { travelDocumentTypes, travelDocumentTypesArray } from '../../utils/const.js';
import { useState, useEffect } from 'react';
import "../../css/AddTravelDocumentDialog.scss"

const useStyles = makeStyles(() => ({
    paper: { minWidth: "700px" },
}));

function AddTravelDocumentDialog(props) {

    const classes = useStyles();
    const { open, onClose, refreshTravelDocumentList, documentList } = props
    const { handleRequest } = useHttp();
    const employees = getEmployeeEmails()
    const [email, setEmail] = useState(null)
    const [documentType, setDocumentType] = useState(null)
    const [documentNumber, setDocumentNumber] = useState(null)
    const [country, setCountry] = useState(null)
    const [issuedDate, setIssuedDate] = useState(null)
    const [expiryDate, setExpiryDate] = useState(null)
    const [documentImage, setDocumentImage] = useState(null)
    const [isFormError, setFormError] = useState(false)
    const [isDateError, setDateError] = useState(false)
    const [isLoading, setLoading] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })

    const handleSubmit = () => {

        setDateError(false)
        setFormError(false)
        if (!email || !country || (documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && !expiryDate) || !documentImage || !documentNumber || !documentType) {
            setFormError(true)
            setSnackbarData({ message: "Please check the form again", open: true, severity: "error", onClose: onCloseSnackbar })
        }
        else {
            setFormError(false)
            if ((documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT) && (issuedDate > expiryDate)) {
                setSnackbarData({ message: "Please check the dates again", open: true, severity: "error", onClose: onCloseSnackbar })
                setDateError(true)
                setFormError(true)
            }
            else {

                let exitingDocuments = documentList.filter(document => document.documentType === documentType && document.issuedCountry === country.countryCode && document.email === email)

                let contentMsg = exitingDocuments.length > 0
                    ? "There is an already existing Travel Document for the same User for the same Country with same Document Type. This will override that existing Travel Document and it can not be undone. Please make sure the information are correct. Are you sure you want to override and add this Travel Document?"
                    : "Please make sure the information are correct. Are you sure you want to add this Visa?"


                setConfirmationDialogData({
                    open: true,
                    title: "Add New Travel Document",
                    content: contentMsg,
                    acceptLabel: "Yes",
                    declineLabel: "No",
                    acceptAlert: saveDocument,
                    declineAlert: handleDeclineAlert
                })
            }
        }
    }

    const saveDocument = () => {

        setConfirmationDialogData(data => ({ ...data, open: false }))
        onClose()
        setLoading(true)

        const body = {
            email: email,
            documentType: documentType,
            documentNo: documentNumber,
            issuedCountry: country.countryCode,
            issuedDate: getDateForDB(issuedDate),
            expiryDate: getDateForDB(expiryDate)
        }

        handleRequest(postEmployeeTravelDocument, "POST", body, (response) => {
            if (response.id > 0) {

                let fileExtension = documentImage.name.substring(documentImage.name.lastIndexOf('.') + 1, documentImage.name.length)
                let fileName = country.countryCode + "." + fileExtension;

                const reader = new FileReader()
                reader.readAsDataURL(documentImage);
                reader.onload = () => {
                    const encodedFile = btoa(reader.result)
                    const fileBody = {
                        encodedFile: encodedFile
                    }

                    handleRequest(postTravelDocumentImage.replace("$documentId", response.id).replace("$imageName", fileName), "POST", fileBody, (document) => {
                        if (document.id > 0) {
                            handleClose()
                            refreshTravelDocumentList()
                        }
                        else {
                            setSnackbarData({ message: "Travel document image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                        }
                        setLoading(false)
                    }, (error) => {
                        console.error(error)
                        setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                        setLoading(false)
                    })
                };
            }
            else {
                setSnackbarData({ message: "Travel document image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                setLoading(false)
            }
        }, (error) => {
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            setLoading(false)
        })
    }

    const fileUploadSizeError = () => {
        console.error("Size cannot exceed 1MB")
        setSnackbarData({ message: "Size cannot exceed 1MB", open: true, severity: "error", onClose: onCloseSnackbar })
        setDocumentImage(null)
    }

    const handleClose = () => {
        onClose()
        setEmail(null)
        setDocumentNumber(null)
        setCountry(null)
        setExpiryDate(null)
        setIssuedDate(null)
        setDocumentImage(null)
        setFormError(false)
        setDateError(false)
        onCloseSnackbar()
        setLoading(false)
    }

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (

        <div>

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

            <CustomSnackbar snackbar={snackbarData} />

            <BackdropProgress open={isLoading} />

            <Dialog onClose={() => handleClose()} open={open} classes={{ paper: classes.paper }} className="Add-travel-document-dialog">

                <div className='Dialog-div'>

                    <div className='Dialog-title'>Add Travel Document</div>

                    <div>
                        <div className='Fields-required'><span className='Required-span'>*</span> All fields are required</div>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Email</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <Autocomplete
                                    loading
                                    size="small"
                                    variant="standard"
                                    fullWidth
                                    disablePortal
                                    isOptionEqualToValue={(option, value) => option.workEmail === value.workEmail}
                                    options={employees.filter(emp => emp.workEmail !== null)}
                                    getOptionLabel={(option) => option.workEmail}
                                    onChange={(event, value) => setEmail(value.workEmail)}
                                    renderOption={(props, option) => (
                                        <Box {...props} key={option.id}>{option.workEmail}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Traveller's Email" error={isFormError && !email} />}
                                />
                            </Grid>
                        </Grid>



                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Type</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    noOptionsText='No options'
                                    disablePortal
                                    isOptionEqualToValue={(option, value) => option === value}
                                    fullWidth
                                    onChange={(event, value) => setDocumentType(value)}
                                    options={travelDocumentTypesArray}
                                    getOptionLabel={(option) => option}
                                    renderOption={(props, option) => (
                                        <Box {...props}>{option}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Document Type" error={isFormError && !documentType} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Number</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <TextField size="small" fullWidth label="Document Number" onChange={(event) => setDocumentNumber(event.target.value)} error={isFormError && !documentNumber} />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Issued Country</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    noOptionsText='No options'
                                    disablePortal
                                    isOptionEqualToValue={(option, value) => option.country === value.country}
                                    fullWidth
                                    onChange={(event, value) => setCountry(value)}
                                    options={getCountryList()}
                                    getOptionLabel={(option) => option.country}
                                    renderOption={(props, option) => (
                                        <Box {...props}>{option.country}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Issued Country" error={isFormError && !country} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Issued Date</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Issued Date"
                                        inputFormat='yyyy-MM-dd'
                                        mask='____-__-__'
                                        value={issuedDate}
                                        onChange={(value) => setIssuedDate(value)}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton onClick={() => setIssuedDate(null)} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: "start"
                                        }}
                                        renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !issuedDate) || isDateError} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'>
                                <span className='Grid-item-label'>Expiry Date</span>
                                {documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && <span className='Grid-item-required'>*</span>}
                            </Grid>
                            <Grid item md={8} className='Grid-item'>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Expiry Date"
                                        inputFormat='yyyy-MM-dd'
                                        mask='____-__-__'
                                        value={expiryDate}
                                        onChange={(value) => setExpiryDate(value)}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton onClick={() => setExpiryDate(null)} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: "start"
                                        }}
                                        renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && !expiryDate) || isDateError} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Image</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={8} className='Grid-item'>
                                <div className={(isFormError && !documentImage) ? "File-upload-div-error" : "File-upload-div"}>
                                    <FileUploader handleChange={file => (setDocumentImage(file))} maxSize={1} onSizeError={() => fileUploadSizeError()}
                                        name="file" label="Browse or Drag and Drop Image. Please upload an image of the document details page (Max Size: 1Mb)." types={["JPG", "PNG", "JPEG"]} />
                                </div>
                            </Grid>
                        </Grid>
                        <div className='Button-grid'>
                            <Button variant="contained" color="secondary" className="Submit-button" onClick={() => handleSubmit()}>Submit</Button>
                            <Button variant="outlined" color="primary" className="Reset-button" onClick={() => handleClose()}>Cancel</Button>
                        </div>
                    </div>

                </div>
            </Dialog >

        </div>

    );
}

export default AddTravelDocumentDialog;
